/* eslint-disable react-hooks-extra/no-direct-set-state-in-use-effect */
import type { ComponentPropsWithRef } from 'react';
import type { ColorPickerProps } from 'react-best-gradient-color-picker';

import { Button } from '@connected/ui/components/button';
import { cn } from '@connected/ui/lib/utils';
import { ClipboardCopy, ClipboardPaste } from 'lucide-react';
import { useEffect, useState } from 'react';
import ColorPicker from 'react-best-gradient-color-picker';

import type { PrimativeFieldProps } from '../../lib/utils';

export type GradientPickerValueType = string | null | undefined;

export type GradientPickerProps<T extends GradientPickerValueType> = Omit<ColorPickerProps, 'id' | 'name' | 'value' | 'onChange' | 'onBlur' | 'disabled'> & PrimativeFieldProps<T>;

const DEFAULT_GRADIENT = 'rgba(255,255,255,1)' as const;

function formatGradientString(gradient: GradientPickerValueType) {
    const formatted = gradient?.replaceAll(';', '');
    return formatted?.length ? formatted : null;
}

export function GradientPicker<T extends GradientPickerValueType>(props: GradientPickerProps<T>) {
    const {
        name,
        value,
        onChange,
        onBlur,
        disabled,
        id,
        disableDarkMode = true,
        ...restProps
    } = props;

    const [gradient, setGradient] = useState<string>(() => formatGradientString(value) || DEFAULT_GRADIENT);

    // Handle incoming value changes
    useEffect(() => {
        if (value === null || value === undefined) {
            setGradient(DEFAULT_GRADIENT);
        }
        else {
            setGradient(formatGradientString(value) || DEFAULT_GRADIENT);
        }
    }, [value]);

    // Handle gradient selection based on the type
    function handleGradientSelect(selectedGradient: string | undefined) {
        if (!selectedGradient)
            return;

        // Pass gradient object directly for gradient type
        onChange(selectedGradient as T);

        if (onBlur) {
            onBlur();
        }
    };

    return (
        <ColorPicker
            key={id ?? name}
            value={gradient}
            onChange={handleGradientSelect}
            disableDarkMode={disableDarkMode}
            {...restProps}
        />
    );
}

GradientPicker.displayName = 'GradientPicker';

export type GradientPickerTriggerProps = Omit<ComponentPropsWithRef<typeof Button>, 'children'> & {
    gradient?: GradientPickerValueType;
};

export function GradientPickerTrigger(props: GradientPickerTriggerProps) {
    const { gradient, onChange, className, ...restProps } = props;

    return (
        <Button
            variant="input"
            type="button"
            className={cn('w-full h-10 rounded-md border border-input bg-background p-2 flex items-center justify-between cursor-pointer', className)}
            {...restProps}
        >
            <div
                className="h-full w-full rounded-md"
                style={{
                    background: formatGradientString(gradient) || DEFAULT_GRADIENT,
                }}
            />
        </Button>
    );
}

GradientPicker.Trigger = GradientPickerTrigger;
GradientPickerTrigger.displayName = 'GradientPickerTrigger';

export type GradientPickerCopyProps = Omit<ComponentPropsWithRef<typeof Button>, 'children'> & {
    gradient?: GradientPickerValueType;
};

export function GradientPickerCopy(props: GradientPickerCopyProps) {
    const { gradient, onChange, className, ...restProps } = props;

    return (
        <Button
            variant="outline"
            onClick={async () => {
                await navigator.clipboard.writeText(gradient || '');
            }}
            className={className}
            {...restProps}
        >
            <ClipboardCopy />
        </Button>
    );
}

GradientPickerCopy.displayName = 'GradientPickerCopy';
GradientPicker.Copy = GradientPickerCopy;

export type GradientPickerPasteProps<T extends GradientPickerValueType> = Omit<ComponentPropsWithRef<typeof Button>, 'children'> & {
    onChange?: GradientPickerProps<T>['onChange'];
};

export function GradientPickerPaste<T extends GradientPickerValueType>(props: GradientPickerPasteProps<T>) {
    const { onChange, className, ...restProps } = props;
    return (
        <Button
            variant="outline"
            onClick={async () => {
                const text = await navigator.clipboard.readText();
                onChange?.(text as T);
            }}
            className={className}
            {...restProps}
        >
            <ClipboardPaste />
        </Button>
    );
}

GradientPickerPaste.displayName = 'GradientPickerPaste';
GradientPicker.Paste = GradientPickerPaste;
